import { SCENARIOS } from "../data/Scenarios";
import manifest from "../data/RunCompatibility.json";
import { getScenarioLocation } from "./Locations";
import { getStartingCustomers } from "../data/LocationProfiles";
import { isValidDifficulty } from "./Difficulty";
import {
  AuthoredRunReference,
  GameType,
  RunIdentity,
  ScenarioType,
  DifficultyType,
} from "../Types";

/** Stable object keys, ordered arrays; functions are covered by the build manifest. */
export function normalizedInputs(value: unknown): string {
  return JSON.stringify(value, (_key, item) =>
    item && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(
          Object.keys(item)
            .sort()
            .map((key) => [key, item[key]]),
        )
      : item,
  );
}
export function captureRunIdentity(
  scenario: ScenarioType,
  seed: number,
  difficulty: DifficultyType,
  inputs?: Partial<RunIdentity["inputs"]>,
  origin: RunIdentity["origin"] = "authored",
): RunIdentity {
  const location = getScenarioLocation(scenario)!;
  return {
    scenarioId: scenario.id,
    // The manifest includes complete authored definitions, including function-valued rules.
    compatibilityId: manifest.compatibilityId,
    seed,
    difficulty,
    origin,
    inputs: {
      scenario: normalizedInputs(scenario),
      location,
      facilities: scenario.facilities,
      cash: scenario.cash,
      customers: scenario.startingCustomers || getStartingCustomers(location),
      meaningfulDecisionGateWaived: false,
      ...inputs,
    },
  };
}
export function sameRunIdentity(
  a: RunIdentity | undefined,
  b: RunIdentity | undefined,
): boolean {
  if (!a || !b) return false;
  const { origin: _a, ...left } = a;
  const { origin: _b, ...right } = b;
  return normalizedInputs(left) === normalizedInputs(right);
}
export function expandAuthoredRunReference(
  raw: unknown,
): RunIdentity | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return;
  const r = raw as AuthoredRunReference;
  if (
    Object.keys(r).sort().join() !==
      ["scenarioId", "seed", "difficulty", "compatibilityId"].sort().join() ||
    r.compatibilityId !== manifest.compatibilityId ||
    !Number.isInteger(r.seed) ||
    r.seed < 0 ||
    r.seed > 0xffffffff ||
    !isValidDifficulty(r.difficulty)
  )
    return;
  const scenario = SCENARIOS.find(
    (s) => s.id === r.scenarioId && !s.tutorialSteps,
  );
  if (
    !scenario ||
    !getScenarioLocation(scenario) ||
    (scenario.seed !== undefined && scenario.seed !== r.seed)
  )
    return;
  return captureRunIdentity(scenario, r.seed, r.difficulty);
}
export function projectAuthoredRunReference(
  identity: RunIdentity | undefined,
): AuthoredRunReference | undefined {
  if (!identity || identity.origin !== "authored") return;
  const { scenarioId, seed, difficulty, compatibilityId } = identity;
  const reference: AuthoredRunReference = {
    scenarioId,
    seed,
    difficulty,
    compatibilityId,
  };
  return sameRunIdentity(identity, expandAuthoredRunReference(reference))
    ? reference
    : undefined;
}
/**
 * Links outlive deploys: an invitation from an earlier build names the same scenario, seed and
 * difficulty under the running rules. The rest of the invitation is validated as usual.
 */
export function carryInvitationForward(raw: unknown): unknown {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  const { run } = raw as { run?: unknown };
  if (!run || typeof run !== "object" || Array.isArray(run)) return raw;
  return {
    ...raw,
    run: { ...run, compatibilityId: manifest.compatibilityId },
  };
}
/**
 * Carry an authored run saved under earlier rules forward to the running ones. Deploys change
 * the rules fingerprint several times a day, and that must not end a run, its challenge, its
 * replay or its score. A run whose scenario no longer describes the game keeps its progress
 * without the shareable identity and challenge. A challenge that never matched its own run is
 * left unchanged for validation to reject.
 */
export function carryRunForward<
  T extends Partial<Pick<GameType, "runIdentity" | "challenge" | "location">>,
>(game: T): T {
  const previous = game.runIdentity;
  if (!previous || previous.compatibilityId === manifest.compatibilityId)
    return game;
  const { scenarioId, seed, difficulty } = previous;
  const identity = expandAuthoredRunReference({
    scenarioId,
    seed,
    difficulty,
    compatibilityId: manifest.compatibilityId,
  });
  if (
    !identity ||
    normalizedInputs(identity.inputs.location) !==
      normalizedInputs(game.location)
  )
    return { ...game, runIdentity: undefined, challenge: undefined };
  const run = (game.challenge as { run?: Record<string, unknown> } | undefined)
    ?.run;
  const challenge =
    game.challenge &&
    run &&
    (["scenarioId", "seed", "difficulty", "compatibilityId"] as const).every(
      (key) => run[key] === previous[key],
    )
      ? { ...game.challenge, run: projectAuthoredRunReference(identity)! }
      : game.challenge;
  return { ...game, runIdentity: identity, challenge };
}
/** Only canonical authored runs carry a shareable identity. */
export function validRunIdentity(raw: unknown): raw is RunIdentity {
  if (!raw || typeof raw !== "object") return false;
  return !!projectAuthoredRunReference(raw as RunIdentity);
}
